// Afspiller vMix-storyboards. Hver animation styrer én "kanal" på et objekt
// (Fly → position, Reveal → beskæring, Fade → gennemsigtighed, Hidden →
// synlighed). Tilstanden efter et storyboard gælder til det næste, ligesom i vMix.
//
// Målt mod vMix (optagelse fra maj 2026): Fly flytter et helt lærreds bredde/
// højde, og Duration er sekunder. Ikke målt endnu, kun antaget: standard-
// varighed, standardretning, og hvor Reveal starter fra.

import type { GtAnim, GtSpec } from "./spec.js";

export const DEFAULT_DURATION = 1;
// Uden TransitionOut fader vMix overlayet ud lineært på 0,5 s (målt).
export const OUT_FADE_SEC = 0.5;

export interface ElState {
  visible: boolean;
  fade: number; // 0–1, ganges på elementets egen opacity
  clip: [number, number, number, number]; // top, højre, bund, venstre som andel af størrelsen
  dx: number;
  dy: number;
}

export type StageState = Record<string, ElState>;

const SHOWN: ElState = { visible: true, fade: 1, clip: [0, 0, 0, 0], dx: 0, dy: 0 };

// Elementer der er skjult i selve vMix-titlen (Visible="False"). De bliver
// skjult, også når et storyboard animerer dem (fx "country"-rækken i H2H).
const hiddenInDesign = new WeakMap<GtSpec, Set<string>>();
function designHidden(spec: GtSpec): Set<string> {
  let set = hiddenInDesign.get(spec);
  if (!set) {
    set = new Set(spec.layers.flatMap((l) => l.elements.filter((e) => !e.visible).map((e) => e.name)));
    hiddenInDesign.set(spec, set);
  }
  return set;
}

export function baseState(spec: GtSpec): StageState {
  const s: StageState = {};
  for (const layer of spec.layers) {
    s[layer.name] = SHOWN;
    for (const el of layer.elements) s[el.name] = { ...SHOWN, visible: el.visible };
  }
  return s;
}

// --- Interpolation (Penner-kurverne vMix bruger)

const power = (n: number) => ({
  in: (t: number) => t ** n,
  out: (t: number) => 1 - (1 - t) ** n,
  inOut: (t: number) => (t < 0.5 ? 2 ** (n - 1) * t ** n : 1 - (-2 * t + 2) ** n / 2),
});

const FAMILIES: Record<string, { in: (t: number) => number; out: (t: number) => number; inOut: (t: number) => number }> = {
  Quadratic: power(2),
  Cubic: power(3),
  Quartic: power(4),
  Quintic: power(5),
  Sine: {
    in: (t) => 1 - Math.cos((t * Math.PI) / 2),
    out: (t) => Math.sin((t * Math.PI) / 2),
    inOut: (t) => -(Math.cos(Math.PI * t) - 1) / 2,
  },
  Exponential: {
    in: (t) => (t === 0 ? 0 : 2 ** (10 * t - 10)),
    out: (t) => (t === 1 ? 1 : 1 - 2 ** (-10 * t)),
    inOut: (t) => (t === 0 || t === 1 ? t : t < 0.5 ? 2 ** (20 * t - 10) / 2 : (2 - 2 ** (-20 * t + 10)) / 2),
  },
  Circular: {
    in: (t) => 1 - Math.sqrt(1 - t * t),
    out: (t) => Math.sqrt(1 - (t - 1) ** 2),
    inOut: (t) => (t < 0.5 ? (1 - Math.sqrt(1 - (2 * t) ** 2)) / 2 : (Math.sqrt(1 - (-2 * t + 2) ** 2) + 1) / 2),
  },
};

export function ease(name: string | null, t: number): number {
  const m = name?.match(/^(\w+?)Easing(InOut|In|Out)$/);
  const family = m && FAMILIES[m[1]!];
  if (!family) return t; // Linear eller ukendt
  return m[2] === "InOut" ? family.inOut(t) : m[2] === "In" ? family.in(t) : family.out(t);
}

// --- Storyboards

const CHANNEL: Record<string, keyof ElState | null> = {
  Fly: "dx",
  Reveal: "clip",
  Fade: "fade",
  Hidden: "visible",
  None: null,
};

const dur = (a: GtAnim) => a.duration ?? DEFAULT_DURATION;

export function storyboardLength(anims: GtAnim[] | undefined): number {
  return Math.max(0, ...(anims ?? []).map((a) => a.delay + dur(a)));
}

function progress(a: GtAnim, t: number): number {
  const d = dur(a);
  if (d <= 0) return t >= a.delay ? 1 : 0;
  return Math.min(1, Math.max(0, (t - a.delay) / d));
}

function applyAnim(spec: GtSpec, s: ElState, a: GtAnim, t: number): ElState {
  const e = ease(a.easing, progress(a, t));
  switch (a.type) {
    case "Fly": {
      // Hvor langt væk objektet er: 1 = helt ude, 0 = på plads.
      const away = a.reverse ? e : 1 - e;
      const [ux, uy] =
        a.direction === "Right" ? [1, 0] : a.direction === "Top" ? [0, -1] : a.direction === "Bottom" ? [0, 1] : [-1, 0];
      return { ...s, dx: ux * spec.width * away, dy: uy * spec.height * away };
    }
    case "Reveal": {
      // Andel der er skjult; Direction er siden afsløringen starter fra.
      const hidden = 1 - (a.reverse ? 1 - e : e);
      const clip: ElState["clip"] =
        a.direction === "Right"
          ? [0, 0, 0, hidden]
          : a.direction === "Top"
            ? [0, 0, hidden, 0]
            : a.direction === "Bottom"
              ? [hidden, 0, 0, 0]
              : a.direction === "Center"
                ? [0, hidden / 2, 0, hidden / 2]
                : [0, hidden, 0, 0];
      return { ...s, clip };
    }
    case "Fade":
      return { ...s, fade: a.reverse ? 1 - e : e };
    case "Hidden":
      return { ...s, visible: false };
    default:
      return s;
  }
}

// Tilstanden t sekunder inde i et storyboard. Har et objekt flere animationer
// på samme kanal, gælder den seneste der er gået i gang — før nogen er i gang,
// står objektet i den førstes udgangspunkt.
export function evaluate(spec: GtSpec, anims: GtAnim[] | undefined, start: StageState, t: number): StageState {
  const out: StageState = { ...start };
  const groups = new Map<string, GtAnim[]>();
  for (const a of anims ?? []) {
    const ch = CHANNEL[a.type];
    if (!ch || !(a.object in out)) continue;
    const key = `${a.object}\u0000${ch}`;
    groups.set(key, [...(groups.get(key) ?? []), a]);
  }
  const hiddenHere = new Set((anims ?? []).filter((a) => a.type === "Hidden").map((a) => a.object));
  for (const list of groups.values()) {
    list.sort((x, y) => x.delay - y.delay);
    const active = list.filter((a) => a.delay <= t).at(-1) ?? list[0]!;
    let s = applyAnim(spec, out[active.object]!, active, t);
    // Animeres et objekt ind, er det synligt — medmindre storyboardet skjuler
    // det, eller det er skjult i designet.
    if (active.type !== "Hidden" && !hiddenHere.has(active.object) && !designHidden(spec).has(active.object)) {
      s = { ...s, visible: true };
    }
    out[active.object] = s;
  }
  return out;
}

export function endState(spec: GtSpec, anims: GtAnim[] | undefined, start: StageState): StageState {
  return evaluate(spec, anims, start, storyboardLength(anims));
}

// Slutstillingen efter en række storyboards, fx ["in", "page1"].
export function stateAfter(spec: GtSpec, steps: string[]): StageState {
  let s = baseState(spec);
  for (const step of steps) {
    if (step === "in") s = baseState(spec);
    s = endState(spec, spec.storyboards[step as keyof GtSpec["storyboards"]], s);
  }
  return s;
}
